import {
  FormatListNumbered,
  InfoOutlined,
  SignalCellularAlt,
} from '@mui/icons-material';
import { Box, Skeleton, Stack, styled, Switch, Tooltip } from '@mui/material';
import { useWindowWidth } from '@react-hook/window-size';
import Bowser from 'bowser';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';
import { ChartRanking, homeStyles } from '..';
import { AnimateIn } from '../../../components/animate-in';
import { toCurrency } from '../../../utils/formatter';

const detectMobile = (): boolean => {
  const parser = Bowser.getParser(window.navigator.userAgent);
  return parser.getPlatform().type === 'mobile';
};

declare var require: NodeRequire;
require('highcharts/highcharts-more')(Highcharts);

export function Chart(props: {
  AgtNo: string;
  Title: string;
  data: ChartRanking[];
  ToolTip?: string | null;
  isCurrency?: boolean;
  mobileOpen?: string | null;
  cantView?: boolean;
}) {
  // Props
  const { AgtNo, Title, data, isCurrency, mobileOpen, cantView } = props;
  // Hooks
  const width = useWindowWidth();
  const isMobile = detectMobile();
  // State
  const [toggle, setToggle] = React.useState(false);

  React.useEffect(() => {
    setToggle(mobileOpen === 'list' ? true : false);
  }, [mobileOpen]);

  const CustomSwitch = styled(Switch)({
    '.MuiSwitch-thumb': {
      backgroundColor: '#e3e5e8',
    },
    '.MuiSwitch-track': {
      backgroundColor: '#090a0b',
    },
    '& .Mui-checked': {
      '.MuiSwitch-thumb': {
        backgroundColor: '#e3e5e8',
      },
      '+ .MuiSwitch-track': {
        backgroundColor: '#090a0b',
      },
    },
  });

  const chart = {
    chart: {
      type: 'column',
      backgroundColor: '#00000000',
      width: isMobile ? width * 0.65 : 350,
      height: width < 400 ? '300px' : null,
    },
    title: {
      text: '',
    },
    plotOptions: {
      column: {
        minPointLength: 3,
      },
    },

    xAxis: {
      type: 'category',
      lineWidth: 0,
      labels: {
        rotation: [-45],
        useHTML: true,
        formatter: function (x: { value: string }) {
          // Find the matching AgtNo
          const isAgent = data.find(
            (i) => i.AgtName === x.value && i.AgtNo === AgtNo,
          );
          return `<span style="color: ${
            isAgent ? '#12D88E' : '#fff'
          }; font-weight: ${isAgent ? 'bold' : 'normal'};
          font-size:${x.value.length > 15 ? '6px' : '8px'};font-family:'Lato';">
            ${x.value}
          </span>`;
        },
      },
    },
    yAxis: {
      min: 0,
      gridLineWidth: 0,
      title: {
        text: '',
      },
      labels: {
        style: {
          color: '#fff',
          fontSize: width < 400 ? '8px' : '10px',
          fontWeight: 'bold',
        },
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      useHTML: true,
      backgroundColor: '#17191B',
      borderRadius: 25,
      borderWidth: 3,
      style: {
        color: '#fff',
        display: 'flex',
      },
      // @ts-ignore
      formatter: function () {
        // @ts-ignore
        const value = this.point.y;
        // @ts-ignore
        const name = this.point.options.name;
        return `
        <div style="text-transform:none;padding:5px;display:flex;flex-direction:column;align-items:center;justify-content:center;">
        <span style="font-size:12px;">${name}</span>
        <span style="font-size:14px;">${
          isCurrency
            ? toCurrency({
                value: value,
              })
            : value
        }</span> 
        </div>`;
      },
    },
    series: [
      {
        groupPadding: 0,
        borderWidth: 0,
        borderRadius: 5,
        data: data.map((i) => {
          const isHighlighted = i.AgtNo === AgtNo; // Check if AgtNo matches
          return {
            name: i.AgtName,
            y: i.Total,
            color: isHighlighted
              ? {
                  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, // Highlight color
                  stops: [
                    [0, '#00FF99'],
                    [0.5, '#12D88E'],
                    [1, '#388277'],
                  ],
                }
              : {
                  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, // Default color
                  stops: [
                    [0, '#00baff'],
                    [1, '#003451'],
                  ],
                },
          };
        }),
      },
    ],
    credits: {
      enabled: false,
    },
  };

  return (
    <Box
      p={1}
      sx={{
        ...homeStyles.box,
        letterSpacing: 1,
        maxWidth: isMobile ? width * 0.7 : 350,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        filter: cantView ? 'blur(10px)' : '',
        pointerEvents: cantView ? 'none' : 'auto',
        userSelect: cantView ? 'none' : 'auto',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            color: '#12D88E',
            fontWeight: 'bold',
            fontSize: 16,
            fontFamily: 'Lato',
            letterSpacing: 1.1,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {Title ? Title : <Skeleton width="100%" />}

          {props.ToolTip ? (
            <Box sx={{ position: 'absolute', top: 2, right: 2 }}>
              <Tooltip
                title={props.ToolTip || ''}
                arrow
                placement="bottom"
                slotProps={{
                  tooltip: { sx: { backgroundColor: '#17191B', p: 1 } },
                  arrow: { sx: { color: '#17191B' } },
                }}
              >
                <InfoOutlined
                  fontSize="small"
                  sx={{
                    transform: 'scale(.8)',
                    cursor: 'help',
                    color: '#fff',
                  }}
                />
              </Tooltip>
            </Box>
          ) : null}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <SignalCellularAlt
            sx={{ color: '#6f767c', transform: 'scaleX(-1)' }}
          />
          <Tooltip
            title={
              toggle ? 'Switch to Graph View' : 'Switch to Leaderboard View'
            }
            placement="bottom"
            arrow
            slotProps={{
              tooltip: { sx: { backgroundColor: '#17191B', p: 1 } },
              arrow: { sx: { color: '#17191B' } },
            }}
          >
            <CustomSwitch
              size="small"
              value={toggle}
              checked={toggle}
              onChange={() => setToggle(!toggle)}
            />
          </Tooltip>
          <FormatListNumbered sx={{ color: '#6f767c' }} />
        </Box>
      </Box>

      {!toggle ? (
        <HighchartsReact highcharts={Highcharts} options={chart} />
      ) : (
        <AnimateIn to="top" nofade="true" pixels={50}>
          <Stack
            sx={{
              width: isMobile ? width * 0.65 : 350,
              py: 1,
            }}
          >
            {data.map((i, index) => {
              return (
                <Box
                  key={i.AgtName}
                  sx={{
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: i.AgtNo === AgtNo ? '#12D88E' : '#D9D9D9',
                    fontWeight: i.AgtNo === AgtNo ? 700 : 400,
                    backgroundColor: index % 2 ? '#27272775' : '#00000075',
                    py: 2,
                    fontSize: isMobile ? 10 : 14,
                  }}
                >
                  <Box
                    flex={0.5}
                    sx={{ textAlign: 'left', fontWeight: 'bold' }}
                  >
                    {index < 5 ? index + 1 + '.' : '\u2014'}
                  </Box>
                  <Box
                    flex={2}
                    px={1}
                    sx={{
                      textAlign: 'center',
                      textTransform: 'none',
                      fontSize: i.AgtName && i.AgtName.length > 15 ? 12 : 14,
                    }}
                  >
                    {i.AgtName}
                  </Box>

                  <Box flex={0.5} sx={{ textAlign: 'right' }}>
                    {Math.floor(i.Total || 0).toLocaleString()}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </AnimateIn>
      )}
    </Box>
  );
}
